import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { FileText, ExternalLink, Search, ArrowUp, ArrowDown, ArrowUpDown, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COLUMNS = [
  { key: "id", label: "ERX ID", sortable: true, mobile: true },
  { key: "patient_name", label: "Patient", sortable: true, mobile: true },
  { key: "medication_name", label: "Medication", sortable: true, mobile: true },
  { key: "dosage", label: "Dosage", sortable: true, mobile: false },
  { key: "pharmacy_tenant_id", label: "Pharmacy", sortable: true, mobile: false },
  { key: "created_at", label: "Date", sortable: true, mobile: false },
  { key: "status", label: "Status", sortable: true, mobile: true },
];

function compare(a, b) {
  const av = a ?? "";
  const bv = b ?? "";
  if (typeof av === "number" && typeof bv === "number") return av - bv;
  return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
}

function toCsvCell(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function ERXHistoryTab() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  useEffect(() => {
    API.get("/erx-records").then(({ data }) => setRecords(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openPdf = async (erxId) => {
    if (!erxId) { toast.error("Missing ERX ID"); return; }
    try {
      const { data } = await API.post(`/erx/${erxId}/pdf-url`);
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const win = window.open(`${base}${data.path}`, "_blank", "noopener,noreferrer");
      if (!win) toast.error("Browser blocked the popup — allow popups for this site");
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to open eRx PDF"); }
  };

  const setSortKey = (key) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      // Default: dates/ids sort desc-first (newest first), text asc-first.
      const defaultDir = key === "created_at" || key === "id" ? "desc" : "asc";
      return { key, dir: defaultDir };
    });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = records.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (r.id || "").toLowerCase().includes(q) ||
        (r.patient_name || "").toLowerCase().includes(q) ||
        (r.medication_name || "").toLowerCase().includes(q)
      );
    });
    out = [...out].sort((a, b) => {
      const res = compare(a[sort.key], b[sort.key]);
      return sort.dir === "asc" ? res : -res;
    });
    return out;
  }, [records, query, statusFilter, sort]);

  const downloadCsv = () => {
    if (filtered.length === 0) { toast.error("Nothing to export"); return; }
    const headers = ["ERX ID", "Patient", "Medication", "Dosage", "Pharmacy", "Date", "Status"];
    const lines = [headers.join(",")];
    for (const r of filtered) {
      lines.push([
        r.id, r.patient_name, r.medication_name, r.dosage,
        r.pharmacy_tenant_id, r.created_at, r.status,
      ].map(toCsvCell).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `erx-history-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} records`);
  };

  if (loading) return <div className="text-center py-12 text-slate-400 dark:text-slate-500">Loading ERX records...</div>;

/* eslint-disable react/no-unstable-nested-components -- SortIcon is a tiny render-only helper local to this component; extracting it would only add prop plumbing */
  const statuses = ["all", "sent_to_pharmacy", "approved", "pending"];
  const SortIcon = ({ active, dir }) => {
    if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40 inline ml-1" />;
    return dir === "asc" ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>ERX History</h1>
        <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="erx-export-csv-btn">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search by ERX ID, patient, or medication..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="erx-history-search" />
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${statusFilter === s ? "bg-purple-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
              data-testid={`erx-filter-${s}`}
            >
              {s === "all" ? "All" : s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mb-2" data-testid="erx-results-count">
        {filtered.length} {filtered.length === 1 ? "record" : "records"}
        {query && ` matching "${query}"`}
        {statusFilter !== "all" && ` · status: ${statusFilter.replace(/_/g, " ")}`}
      </div>
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="erx-history-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`text-left px-4 py-3 text-slate-600 dark:text-slate-400 font-medium ${col.mobile ? "" : "hidden md:table-cell"} ${col.sortable ? "cursor-pointer select-none hover:text-slate-900 dark:hover:text-white" : ""}`}
                  onClick={col.sortable ? () => setSortKey(col.key) : undefined}
                  data-testid={col.sortable ? `erx-sort-${col.key}` : undefined}
                >
                  {col.label}
                  {col.sortable && <SortIcon active={sort.key === col.key} dir={sort.dir} />}
                </th>
              ))}
              <th className="text-right px-4 py-3 text-slate-600 dark:text-slate-400 font-medium w-[1%] whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {filtered.map((e, idx) => (
              <tr
                key={e.id}
                onClick={() => openPdf(e.id)}
                className={`${idx % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50 dark:bg-slate-800/30"} hover:bg-purple-50 dark:hover:bg-purple-900/20 cursor-pointer transition-colors`}
                data-testid={`erx-row-${e.id}`}
                title="Click to view eRx PDF"
              >
                <td className="px-4 py-3 font-mono text-purple-600 dark:text-purple-300 text-xs">{e.id}</td>
                <td className="px-4 py-3 text-slate-800 dark:text-slate-100">{e.patient_name}</td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{e.medication_name}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400 hidden md:table-cell">{e.dosage || "-"}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400 hidden md:table-cell">{e.pharmacy_tenant_id || "-"}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400 hidden md:table-cell text-xs">
                  {e.created_at ? new Date(e.created_at).toLocaleDateString() : "-"}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700">
                    {(e.status || "").replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(ev) => { ev.stopPropagation(); openPdf(e.id); }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800 dark:text-purple-300 dark:hover:text-white"
                    data-testid={`erx-row-pdf-${e.id}`}
                  >
                    <FileText className="w-3.5 h-3.5" /> PDF <ExternalLink className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {records.length === 0 ? "No ERX records yet" : "No records match your search"}
          </div>
        )}
      </div>
    </div>
  );
}
